import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'

import { useProductShell } from '@/components/sidebar'
import { bookkeeperHref } from '@/config/config'
import { getStoredSidebarTab, setStoredSidebarTab, SidebarTab } from '@/lib/sidebarTab'
import { type SidebarNavItem, useCurrentUser } from '@/models/user'
import { isConsolePath, isProductOffer, resolveSidebarTab } from './navigation'

interface ActiveSidebarTab {
  /** Null until the user has loaded. */
  tab: SidebarTab | null
  /** Swaps the rail's rows without navigating. */
  switchTab: (tab: SidebarTab) => void
  /** The top bar's pick: swaps the rows and opens the tab's first page, where it has one in this app. */
  openTab: (tab: SidebarTab) => void
  /** The tab was picked on this page rather than carried onto it. */
  picked: boolean
}

const ActiveSidebarTabContext = createContext<ActiveSidebarTab>({
  tab: null,
  switchTab: () => {},
  openTab: () => {},
  picked: false,
})

/** One tab for the top bar's strip and the rail's rows, so the two never disagree. */
export function ActiveSidebarTabProvider({ children }: { children: ReactNode }) {
  const { pathname, key: historyKey } = useLocation()
  const navigate = useNavigate()
  const { user } = useCurrentUser({ enabled: true })
  // Counted, not keyed on the entry: Back returns to the entry a pick was made on, and that
  // is still an arrival the URL should decide rather than a replay of an old choice.
  const navigations = useRef(0)
  const lastHistoryKey = useRef(historyKey)
  if (lastHistoryKey.current !== historyKey) {
    lastHistoryKey.current = historyKey
    navigations.current += 1
  }

  const [picked, setPicked] = useState<{ at: number; tab: SidebarTab } | null>(null)
  const [storedTab, setStoredTab] = useState(getStoredSidebarTab)

  const resolved = user
    ? resolveSidebarTab(user, pathname, picked?.at === navigations.current ? picked.tab : null, storedTab)
    : null
  // In the shared shell Bookkeeper and the console are tabs of their own in the bar, so the page decides.
  const inShell = !!useProductShell()
  const tab =
    inShell && user ? (isConsolePath(pathname) ? SidebarTab.Console : SidebarTab.Insurance) : resolved

  useEffect(() => {
    if (!tab || tab === storedTab) return
    setStoredSidebarTab(tab)
    setStoredTab(tab)
  }, [tab, storedTab])

  const switchTab = (next: SidebarTab) => setPicked({ at: navigations.current, tab: next })

  function openTab(next: SidebarTab) {
    // Back from an offer to the tab that owns the page still mounted beneath it: uncover it, don't navigate.
    const pickedHere = picked?.at === navigations.current ? picked.tab : null
    if (pickedHere && user && isProductOffer(pickedHere, user, pathname, true)) {
      if (next === resolveSidebarTab(user, pathname, null, null)) {
        switchTab(next)
        return
      }
    }
    const first = next === SidebarTab.Console ? undefined : firstRowUrl(next)
    // Bookkeeper's pages live on its own origin, so a fleet that has it goes there.
    if (next === SidebarTab.Finance && first) {
      window.location.assign(bookkeeperHref(first))
      return
    }
    if (first?.startsWith('/') && first !== pathname) {
      void navigate(first)
      return
    }
    switchTab(next)
  }

  function firstRowUrl(next: Exclude<SidebarTab, SidebarTab.Console>): string | undefined {
    const rows: Record<typeof next, SidebarNavItem[] | undefined> = {
      [SidebarTab.Insurance]: user?.sidebar.insurance,
      [SidebarTab.Compliance]: user?.sidebar.vault,
      [SidebarTab.Finance]: user?.sidebar.finance,
    }
    return rows[next]?.[0]?.url
  }

  return (
    <ActiveSidebarTabContext.Provider
      value={{ tab, switchTab, openTab, picked: picked?.at === navigations.current }}
    >
      {children}
    </ActiveSidebarTabContext.Provider>
  )
}

export function useActiveSidebarTab(): ActiveSidebarTab {
  return useContext(ActiveSidebarTabContext)
}
